const MONITORED_STATUSES = new Set(['online', 'down', 'offline', 'unknown']);

function normalizeName(value = '') {
  return value.normalize('NFKC').toLowerCase().replace(/[^a-z0-9\u3400-\u9fff]+/g, '');
}

function normalizeHostname(value = '') {
  try {
    return new URL(value).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return '';
  }
}

function isIsoDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value ?? '')) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.valueOf()) && date.toISOString().slice(0, 10) === value;
}

function matchScore(site, record) {
  const siteHost = normalizeHostname(site.url);
  const recordHost = normalizeHostname(record.url);
  if (siteHost && siteHost === recordHost) return 100;

  const identifiers = [site.name, site.source_name, ...(site.aka ?? [])]
    .map(normalizeName)
    .filter((value) => value.length >= 3);
  const recordName = normalizeName(record.sourceName);
  const recordSearch = normalizeName(record.search);
  let score = 0;
  for (const identifier of identifiers) {
    if (identifier === recordName) score = Math.max(score, 90);
    if (identifier === recordSearch) score = Math.max(score, 85);
    if (recordSearch.includes(identifier) || recordName.includes(identifier) || identifier.includes(recordName)) {
      score = Math.max(score, Math.min(80, 40 + identifier.length));
    }
  }
  return score;
}

export function isSavePtSnapshot(snapshot) {
  return snapshot?.schemaVersion === 1
    && typeof snapshot.fetchedAt === 'string'
    && Array.isArray(snapshot.records)
    && snapshot.records.length > 0
    && snapshot.records.every((record) => record?.url && record.sourceName && MONITORED_STATUSES.has(record.status));
}

export function matchSavePtRecords(sites, records) {
  const matches = new Map();
  for (const record of records) {
    const ranked = sites
      .map((site) => ({ site, score: matchScore(site, record) }))
      .filter(({ score }) => score > 0)
      .sort((left, right) => right.score - left.score);
    if (!ranked[0] || ranked[1]?.score === ranked[0].score || matches.has(ranked[0].site.id)) continue;
    matches.set(ranked[0].site.id, record);
  }
  return matches;
}

function mergeSite(site, record, fetchedAt) {
  const communityProfile = site.community_profile ? { ...site.community_profile } : {};
  if (isIsoDate(record.openedAt)) {
    communityProfile.founded_at = record.openedAt;
    if ('founded' in communityProfile) communityProfile.founded = Number(record.openedAt.slice(0, 4));
  }
  if (record.status === 'offline') {
    if (isIsoDate(record.closedAt)) communityProfile.closed_at = record.closedAt;
  } else if (['online', 'down'].includes(record.status)) {
    delete communityProfile.closed_at;
  }

  return {
    ...site,
    ...(Object.keys(communityProfile).length ? { community_profile: communityProfile } : {}),
    monitor: {
      provider: 'savept',
      status: record.status,
      observedAt: fetchedAt,
      sourceName: record.sourceName,
    },
  };
}

export function applySavePtSnapshot(index, snapshot) {
  if (!index || !isSavePtSnapshot(snapshot)) return { index, matchedCount: 0, sourceCount: 0 };
  const matches = matchSavePtRecords(index.sites, snapshot.records);
  return {
    index: {
      ...index,
      sites: index.sites.map((site) => matches.has(site.id) ? mergeSite(site, matches.get(site.id), snapshot.fetchedAt) : site),
    },
    matchedCount: matches.size,
    sourceCount: snapshot.records.length,
  };
}

export function formatMonitorTime(value, language) {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return value;
  return new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'zh-CN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}
